import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  FileText, Copy, Camera, Pencil, Mic, MicOff, CheckCircle2, ChevronRight,
  Send, BookCheck, Brain, Pill, AlertTriangle, ChevronDown, Sparkles, User
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_SOAP_BY_PATIENT = {
  P001: {
    subjective: 'Patient presents for diabetes management follow-up. Reports intermittent headaches over the past 2 weeks, occurring 3-4 times weekly. Rates pain 5/10, frontal location, worse with stress. Taking acetaminophen PRN with partial relief. Denies visual changes, nausea, or neurological symptoms. Blood glucose logs show fasting values 130-160 mg/dL. Compliant with Metformin.',
    objective: 'Vitals: BP 138/88, HR 72, Temp 98.6\u00b0F, SpO2 98%, Weight 185 lbs, BMI 27.2. General: Alert, oriented, no acute distress. HEENT: Normocephalic, PERRLA, no papilledema. Neck: Supple, no lymphadenopathy. Cardiovascular: RRR, no murmurs. Respiratory: Clear bilaterally. Neuro: CN II-XII intact, no focal deficits.',
    assessment: '1. Type 2 Diabetes Mellitus (E11.9) \u2014 suboptimal control, HbA1c 7.2% above target\n2. Essential Hypertension (I10) \u2014 borderline elevated today\n3. Tension-type headache \u2014 likely stress-related\n4. Hyperlipidemia (E78.5) \u2014 stable on current regimen',
    plan: '1. Increase Metformin to 1000mg BID, recheck HbA1c in 3 months\n2. Continue Lisinopril 20mg daily, add home BP monitoring\n3. Acetaminophen 500mg q6h PRN for headaches, stress management counseling\n4. Continue Atorvastatin 20mg, recheck lipid panel in 3 months\n5. Follow-up in 4-6 weeks'
  },
  P002: {
    subjective: 'Patient presents for annual physical examination. No acute complaints. Reports good energy, sleeping well. Denies chest pain, shortness of breath, or GI symptoms.',
    objective: 'Vitals: BP 122/78, HR 68, Temp 98.4\u00b0F, SpO2 99%, Weight 145 lbs. General: Well-appearing female. HEENT: Normal. Cardiovascular: RRR. Respiratory: Clear. Abdomen: Soft, non-tender.',
    assessment: '1. Annual preventive visit \u2014 up to date on screenings\n2. Health maintenance \u2014 good overall health',
    plan: '1. Continue current medications\n2. Schedule routine labs (CBC, CMP, lipid panel)\n3. Flu vaccine administered today\n4. Follow-up in 12 months for annual physical'
  },
  P003: {
    subjective: 'Patient presents with persistent cough and low-grade fever for 5 days. Reports productive cough with yellow sputum. Mild shortness of breath on exertion.',
    objective: 'Vitals: BP 130/82, HR 80, Temp 100.2\u00b0F, SpO2 96%, Weight 200 lbs. General: Mild distress from coughing. HEENT: Oropharynx mildly erythematous. Respiratory: Decreased breath sounds right lower lobe.',
    assessment: '1. Community-acquired pneumonia \u2014 right lower lobe\n2. COPD exacerbation \u2014 acute on chronic',
    plan: '1. Azithromycin 500mg day 1, then 250mg days 2-5\n2. Albuterol inhaler 2 puffs q4h PRN\n3. Increase oral fluids, rest\n4. Chest X-ray ordered\n5. Return in 3 days if not improving'
  },
};

const DEFAULT_SOAP = {
  subjective: 'Patient presents for evaluation. Chief complaint documented.',
  objective: 'Vitals reviewed. Physical examination performed.',
  assessment: 'Assessment documented based on clinical findings.',
  plan: 'Plan documented. Follow-up as indicated.'
};

// AI-generated visit summary per patient
const AI_VISIT_SUMMARIES = {
  P001: "This is John Smith's 4th visit for diabetes management. Last A1c was 7.2% (3 months ago). Previously discussed GLP-1 agonist \u2014 patient declined due to cost. BP has been trending up over the past 3 visits. Current medications: Metformin 1000mg BID, Lisinopril 20mg daily, Atorvastatin 40mg QHS.",
  P002: "Maria Garcia's annual physical. Last visit was 11 months ago for a URI. Up to date on cancer screenings. No chronic conditions. Family history notable for breast cancer (mother).",
  P003: "Robert Johnson presenting with acute illness. Known COPD on Spiriva. Last pulmonary function test 8 months ago showed moderate obstruction. Had 2 exacerbations in the past year.",
};

const HCC_GAPS = {
  P001: ['HCC 19: Diabetes without complication \u2014 document current complications', 'HCC 85: Congestive Heart Failure \u2014 assess if applicable'],
  P003: ['HCC 111: COPD \u2014 specify severity and treatment response'],
};

const NoteReviewLightbox = ({ note, open, onClose }) => {
  const { isDemo } = useAppMode();
  const [edits, setEdits] = useState({});
  const [editingSections, setEditingSections] = useState({});
  const [dictating, setDictating] = useState(null);
  const [showPriorNotes, setShowPriorNotes] = useState(false);

  useEffect(() => {
    if (note && open) {
      const soap = note.soap || DEMO_SOAP_BY_PATIENT[note.patientId] || DEFAULT_SOAP;
      setEdits({
        subjective: soap.subjective || '',
        objective: soap.objective || '',
        assessment: soap.assessment || '',
        plan: soap.plan || ''
      });
      setEditingSections({});
      setDictating(null);
      setShowPriorNotes(false);
    }
  }, [note, open]);

  const handleClose = () => {
    setEdits({});
    setEditingSections({});
    setDictating(null);
    onClose();
  };

  const handleCopy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const patientId = note?.patientId;
  const visitSummary = AI_VISIT_SUMMARIES[patientId] || "Visit context will appear here when patient data is available.";
  const hccGaps = HCC_GAPS[patientId] || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-[90vw] w-[90vw] max-h-[90vh] h-[90vh] p-0 overflow-hidden gap-0" data-testid="note-review-lightbox" aria-describedby="shared-note-review-desc">
        {note && (
          <>
            {/* Header */}
            <div className="bg-slate-900 px-5 py-3.5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-white" />
                <div>
                  <DialogTitle className="text-sm font-bold text-white">{note.chief || note.type || 'Clinical Note'}</DialogTitle>
                  <DialogDescription id="shared-note-review-desc" className="text-[10px] text-slate-400 mt-0.5">
                    {note.date} &middot; {note.provider || 'Dr. Sarah Chen'} &middot; {note.patient || 'Patient'}
                  </DialogDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={cn("text-[9px] border-0", note.signed || note.status === 'completed' || note.status === 'Signed' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300')}>
                  {note.signed ? 'Signed' : note.status === 'completed' ? 'Completed' : note.status || 'Draft'}
                </Badge>
              </div>
            </div>

            {/* Two-Panel Layout */}
            <div className="flex flex-1 overflow-hidden">
              {/* LEFT PANEL — AI Patient Summary (30%) */}
              <div className="w-[30%] border-r border-slate-200 bg-slate-50/50 overflow-y-auto p-4 space-y-4" data-testid="lightbox-ai-summary">
                {/* Patient Info */}
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">
                    <User className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{note.patient || 'Patient'}</p>
                    <p className="text-[10px] text-slate-400">{note.type || 'Visit'} &middot; {note.date}</p>
                  </div>
                </div>

                {/* AI Visit Summary */}
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Brain className="h-3.5 w-3.5 text-violet-500" />
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">AI Visit Summary</p>
                  </div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">{visitSummary}</p>
                </div>

                {/* Current Medications */}
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Pill className="h-3.5 w-3.5 text-blue-500" />
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Medications</p>
                  </div>
                  {patientId === 'P001' ? (
                    <div className="space-y-0.5 text-[11px] text-slate-600">
                      <p>Metformin 1000mg BID</p>
                      <p>Lisinopril 20mg Daily</p>
                      <p>Atorvastatin 40mg QHS</p>
                      <p>Aspirin 81mg Daily</p>
                    </div>
                  ) : <p className="text-[10px] text-slate-400 italic">Medications will auto-populate from chart</p>}
                </div>

                {/* HCC Coding Gaps */}
                {hccGaps.length > 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/30 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                      <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">HCC Coding Gaps</p>
                    </div>
                    <div className="space-y-1">
                      {hccGaps.map((gap, i) => (
                        <p key={i} className="text-[10px] text-amber-700">&bull; {gap}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prior Visit Notes */}
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <button onClick={() => setShowPriorNotes(!showPriorNotes)} className="flex items-center justify-between w-full" data-testid="toggle-prior-notes">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Prior Visit Notes</p>
                    <ChevronDown className={cn("h-3 w-3 text-slate-400 transition-transform", showPriorNotes && "rotate-180")} />
                  </button>
                  {showPriorNotes && (
                    <div className="mt-2 space-y-2 text-[10px] text-slate-500">
                      <div className="p-2 rounded-lg bg-slate-50">
                        <p className="font-semibold text-slate-600">Jan 15, 2026 — Diabetes follow-up</p>
                        <p className="mt-0.5">A1c 7.2%. Discussed lifestyle modifications. Metformin increased.</p>
                      </div>
                      <div className="p-2 rounded-lg bg-slate-50">
                        <p className="font-semibold text-slate-600">Nov 3, 2025 — Annual physical</p>
                        <p className="mt-0.5">All screenings up to date. BP 136/86, discussed monitoring.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT PANEL — SOAP Editor (70%) */}
              <div className="w-[70%] flex flex-col overflow-hidden" data-testid="lightbox-soap-editor">
                {/* Action Bar */}
                <div className="px-5 py-2.5 border-b border-slate-100 bg-white flex items-center gap-2 flex-wrap shrink-0">
                  <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => handleCopy(Object.entries(edits).map(([k, v]) => `${k.toUpperCase()}:\n${v}`).join('\n\n'), 'SOAP Note')} data-testid="review-copy-btn">
                    <Copy className="h-2.5 w-2.5" /> Copy Full Note
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" data-testid="review-lens-btn" onClick={() => toast.info('Lens enrichment', { description: 'Upload a document to enrich this note' })}>
                    <Camera className="h-2.5 w-2.5" /> Enrich via Lens
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => {
                    const anyEditing = ['subjective', 'objective', 'assessment', 'plan'].some(k => editingSections[k]);
                    const newState = {};
                    ['subjective', 'objective', 'assessment', 'plan'].forEach(k => { newState[k] = !anyEditing; });
                    setEditingSections(newState);
                  }} data-testid="review-edit-all-btn">
                    <Pencil className="h-2.5 w-2.5" /> {['subjective', 'objective', 'assessment', 'plan'].some(k => editingSections[k]) ? 'Done Editing' : 'Edit All'}
                  </Button>
                  <div className="flex-1" />
                  <Button size="sm" className="h-7 text-[10px] gap-1 bg-slate-700 text-white hover:bg-slate-800" onClick={() => { toast.success('Note finalized and saved'); handleClose(); }} data-testid="review-finalize-btn">
                    <BookCheck className="h-2.5 w-2.5" /> Finalize Note
                  </Button>
                  <Button size="sm" className="h-7 text-[10px] gap-1 bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => { toast.success('Signed and sent to billing'); handleClose(); }} data-testid="review-sign-billing-btn">
                    <Send className="h-2.5 w-2.5" /> Sign & Send to Billing
                  </Button>
                </div>

                {/* SOAP Sections */}
                <div className="overflow-y-auto flex-1 p-5 space-y-3">
                  {['Subjective', 'Objective', 'Assessment', 'Plan'].map(section => {
                    const key = section.toLowerCase();
                    const isEditing = editingSections[key];
                    const isDictating = dictating === key;
                    return (
                      <div key={section} className={cn("rounded-xl border overflow-hidden transition-all", isDictating ? "border-violet-300 shadow-sm shadow-violet-100/50" : "border-slate-200")} data-testid={`review-section-${key}`}>
                        <div className="flex items-center justify-between px-4 py-2 bg-slate-50/60">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{section}</p>
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => {
                              if (isDictating) { setDictating(null); toast.success('Dictation captured', { description: `${section} updated` }); }
                              else { setDictating(key); setEditingSections(p => ({ ...p, [key]: true })); toast.info('Dictating...', { description: `Speak to edit ${section}` }); }
                            }}
                              className={cn("text-[10px] flex items-center gap-0.5 px-1.5 py-0.5 rounded-md transition-colors", isDictating ? "bg-violet-100 text-violet-700" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100")}
                              data-testid={`review-dictate-${key}`}>
                              {isDictating ? <><MicOff className="h-2.5 w-2.5" /> Stop</> : <><Mic className="h-2.5 w-2.5" /> Dictate</>}
                            </button>
                            <div className="h-3 w-px bg-slate-200" />
                            <button onClick={() => handleCopy(edits[key] || '', section)} className="text-[10px] text-slate-400 hover:text-slate-600 flex items-center gap-0.5">
                              <Copy className="h-2.5 w-2.5" /> Copy
                            </button>
                            <button onClick={() => setEditingSections(p => ({ ...p, [key]: !p[key] }))} className={cn("text-[10px] flex items-center gap-0.5", isEditing ? "text-violet-600 font-semibold" : "text-slate-400 hover:text-slate-600")} data-testid={`review-edit-${key}`}>
                              <Pencil className="h-2.5 w-2.5" /> {isEditing ? 'Done' : 'Edit'}
                            </button>
                          </div>
                        </div>
                        {isDictating && (
                          <div className="px-4 py-1.5 bg-violet-50 border-b border-violet-100 flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-violet-500 animate-pulse" />
                            <p className="text-[10px] text-violet-700 font-medium">Recording \u2014 speak your edits for {section.toLowerCase()}</p>
                          </div>
                        )}
                        {isEditing ? (
                          <div className="p-4">
                            <Textarea value={edits[key] || ''} onChange={e => setEdits(p => ({ ...p, [key]: e.target.value }))}
                              className="min-h-28 font-mono text-sm border-0 shadow-none focus-visible:ring-0 p-0" placeholder={`Edit ${section.toLowerCase()}...`} data-testid={`review-textarea-${key}`} />
                          </div>
                        ) : (
                          <div className="px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                            {edits[key] || <span className="text-slate-400 italic">No content</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NoteReviewLightbox;
